import { el } from '../../core/dom.js';
import { readChatBlocksV2 } from '../../core/flags.js';
import {
  fillMarkdown,
  sameOriginUploadUrl,
  needsAuthedUpload,
  bindProtectedUploadClicks,
  hydrateProtectedMedia,
} from '../../components/markdown.js';
import { extractRunRetryBlocks, renderBlocks, stripBlockFences } from '../../components/blocks/index.js';
import { button } from '../../components/button.js';
import { messageWhen } from './when.js';

export function parseMeta(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw) || {}; } catch { return {}; }
}

function attachmentList(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function isBonnie(message) {
  return !!(message && (message.role === 'assistant' || message.content_type === 'agent_reply'));
}

function safeHref(url) {
  const href = sameOriginUploadUrl(url || '');
  return href.startsWith('/uploads/') ? href : '';
}

function fileCard(file) {
  const name = file.name || 'Attachment';
  const href = safeHref(file.url);
  if (!href) return el('span', { class: 'file-card' }, name);
  const image = String(file.mime || file.type || '').startsWith('image');
  const protectedPath = needsAuthedUpload(href);
  if (image) {
    const img = protectedPath
      ? el('img', { alt: name, 'data-protected-src': href })
      : el('img', { src: href, alt: name });
    const link = el('a', {
      class: 'file-card',
      href: protectedPath ? '#' : href,
      target: '_blank',
      rel: 'noopener',
      ...(protectedPath ? { 'data-protected-href': href } : {}),
    }, img, el('span', {}, name));
    return link;
  }
  return el('a', {
    class: 'file-card',
    href,
    target: '_blank',
    rel: 'noopener',
    download: name,
  }, name);
}

/**
 * @param {object} message
 * @param {{ id?: string }} me
 * @param {{ onSave: Function, onCorrect: Function, onPdf: Function }} actions
 */
export function renderMessage(message, me, actions) {
  const bonnie = isBonnie(message);
  const mine = !bonnie && me && message.user_id && message.user_id === me.id;
  const who = bonnie ? 'Bonnie' : (mine ? 'You' : (message.user_name || message.user_email || 'Teammate'));
  const when = messageWhen(message.created_at);
  const meta = parseMeta(message.metadata);
  const raw = message.content || '';
  // S60b — ```run / ```retry live in content until listed in metadata.blocks.
  const fromMeta = Array.isArray(meta.blocks) ? meta.blocks : [];
  const fromFence = extractRunRetryBlocks(raw);
  const blocks = mergeBlocks(fromMeta, fromFence);
  const showBlocks = readChatBlocksV2(me) && blocks.length > 0;
  const source = showBlocks ? stripBlockFences(raw).trim() : raw;
  const body = el('div', { class: 'md-body' });
  if (source) {
    const analysis = meta.analysis && typeof meta.analysis === 'object' ? meta.analysis : {};
    fillMarkdown(body, source, {
      evidence: meta.evidence && typeof meta.evidence === 'object' ? meta.evidence : {},
      runId: analysis.run_id ? String(analysis.run_id) : '',
      isAdmin: !!(me && me.role === 'admin'),
    });
  }
  if (showBlocks) {
    body.append(renderBlocks(blocks, {
      ...actions,
      userId: (actions && actions.userId) || (me && me.id) || '',
      isAdmin: actions && typeof actions.isAdmin === 'boolean'
        ? actions.isAdmin
        : !!(me && me.role === 'admin'),
    }));
  }
  // §10.1 — only the plan message carries the panel; later kinds link back.
  const progressLink = seeProgressLink(meta.analysis);
  if (progressLink) body.append(progressLink);
  const showBody = !!(source || showBlocks || progressLink);
  const files = attachmentList(message.attachments).map(fileCard);
  const whoLine = el('div', { class: 'msg-who' }, when ? `${who} · ${when}` : who);
  const options = optionTable(meta.options, actions);
  let article;
  if (mine) {
    article = el('article', {
      class: 'msg msg-mine rv',
      'data-message-id': message.id || '',
    }, el('div', { class: 'msg-mine-wrap' },
      el('div', { class: 'msg-bubble' }, showBody ? body : null, files.length ? el('div', { class: 'msg-files' }, files) : null),
      whoLine));
  } else {
    const col = el('div', { class: 'msg-col' },
      whoLine,
      showBody ? body : null,
      options,
      files.length ? el('div', { class: 'msg-files' }, files) : null);
    if (bonnie) {
      col.append(el('div', { class: 'msg-actions' },
        button('Save as document', { onClick: (event) => actions.onSave(message, event.currentTarget) }),
        button('Correct me', { onClick: () => actions.onCorrect(message) }),
        button('PDF', { onClick: (event) => actions.onPdf(message, event.currentTarget) }),
        meta.model ? el('span', { class: 'msg-model' }, String(meta.model)) : null));
    }
    article = el('article', {
      class: bonnie ? 'msg msg-bonnie rv' : 'msg msg-other rv',
      'data-message-id': message.id || '',
    }, bonnie ? el('span', { class: 'msg-mark spark', 'aria-hidden': 'true' }, '✦') : null, col);
  }
  if (files.length) {
    bindProtectedUploadClicks(article);
    hydrateProtectedMedia(article);
  }
  return article;
}

function optionTable(options, actions) {
  if (!Array.isArray(options) || !options.length) return null;
  return el('div', { class: 'opt-table' }, options.map((row) => el('button', {
    type: 'button',
    class: 'opt-row',
    onClick: () => actions.onOption && actions.onOption(row),
  },
  el('span', { class: 'opt-k' }, row.label || ''),
  el('span', { class: 'opt-t' }, row.text || row.body || ''),
  row.hint ? el('span', { class: 'opt-h' }, row.hint) : null)));
}

/**
 * Prefer metadata.blocks; add fence-only run/retry that are not already listed.
 * @param {object[]} fromMeta
 * @param {object[]} fromFence
 * @returns {object[]}
 */
function mergeBlocks(fromMeta, fromFence) {
  if (!fromFence.length) return fromMeta;
  if (!fromMeta.length) return fromFence;
  const have = new Set(
    fromMeta
      .filter((b) => b && (b.type === 'run' || b.type === 'retry'))
      .map((b) => `${b.type}:${(b.resolved && b.resolved.run_id) || ''}`)
  );
  const extra = fromFence.filter((b) => !have.has(`${b.type}:${b.resolved.run_id || ''}`));
  return extra.length ? fromMeta.concat(extra) : fromMeta;
}

/**
 * @param {{ run_id?: string, kind?: string }|null|undefined} analysis
 * @returns {HTMLElement|null}
 */
function seeProgressLink(analysis) {
  if (!analysis || typeof analysis !== 'object') return null;
  const kind = String(analysis.kind || '');
  // Only status lines (e.g. a failure) link back to the run card; the report's
  // own messages stay clean.
  if (kind !== 'status') return null;
  const runId = analysis.run_id != null ? String(analysis.run_id) : '';
  if (!runId) return null;
  const link = el('a', {
    class: 'chat-run-see-progress',
    href: `#analysis-run-${encodeURIComponent(runId)}`,
  }, 'See progress');
  link.addEventListener('click', (event) => {
    event.preventDefault();
    const target = typeof document !== 'undefined'
      ? document.getElementById(`analysis-run-${runId}`)
      : null;
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  });
  return link;
}
